import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LanguageContext";
import { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Phone, Languages, ShieldCheck, Loader2 } from "lucide-react";

export default function Login() {
  const { login } = useAuth();
  const { t, toggle, lang } = useLang();
  const navigate = useNavigate();
  const [badge, setBadge] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const u = await login(badge, password);
      navigate(u.role === "admin" ? "/admin" : "/dashboard");
    } catch (err) {
      setError(apiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      <div className="hidden lg:flex lg:w-1/2 bg-[#0F172A] text-white flex-col justify-between p-12 relative overflow-hidden">
        <div
          className="absolute inset-0 opacity-20 bg-cover bg-center"
          style={{ backgroundImage: "url(https://images.unsplash.com/photo-1760872645513-63b6846ce3c9?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200)" }}
        />
        <div className="relative z-10 flex items-center gap-3">
          <img src="/emblem-gold.png" alt="National Emblem of India" className="h-14 w-auto object-contain shrink-0" />
          <div>
            <div className="text-[11px] uppercase tracking-wide text-slate-400">{t("govOfIndia")} · {t("mha")}</div>
            <div className="font-head font-bold text-lg leading-tight">{t("appName")}</div>
          </div>
        </div>
        <div className="relative z-10">
          <h1 className="font-head text-3xl font-bold leading-tight mb-4">
            Indian Cyber Crime<br />Coordination Centre
          </h1>
          <p className="text-slate-300 max-w-md">
            A safe, repeatable sandbox to train 1930 / NCRP call takers — reduce categorization
            and data-entry errors, and practise victim handling with confidence.
          </p>
          <div className="mt-8 flex items-center gap-2 text-sm text-slate-400">
            <ShieldCheck className="h-4 w-4" /> Internal training use only · Synthetic data
          </div>
        </div>
        <div className="relative z-10 text-xs text-slate-500">© I4C Training Simulator · Pilot</div>
      </div>

      <div className="flex-1 flex items-center justify-center p-8 bg-background">
        <div className="w-full max-w-md">
          <div className="flex justify-between items-center mb-8">
            <div className="lg:hidden flex items-center gap-2">
              <Phone className="h-6 w-6 text-blue-600" />
              <span className="font-head font-bold">{t("appName")}</span>
            </div>
            <button
              data-testid="lang-toggle-login"
              onClick={toggle}
              className="ml-auto inline-flex items-center gap-2 rounded-full border border-slate-300 px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50"
            >
              <Languages className="h-4 w-4" /> {t("lang")}
            </button>
          </div>
          <h2 className="font-head text-2xl font-bold text-slate-900">{t("login")}</h2>
          <p className="text-slate-500 mb-6 text-sm">{t("signIn")}</p>

          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label htmlFor="badge">{t("badgeNumber")}</Label>
              <Input
                id="badge" data-testid="login-badge-input" value={badge}
                onChange={(e) => setBadge(e.target.value)} placeholder="e.g. TRN1930"
                className="mt-1.5" autoComplete="username" required
              />
            </div>
            <div>
              <Label htmlFor="pw">{t("password")}</Label>
              <Input
                id="pw" data-testid="login-password-input" type="password" value={password}
                onChange={(e) => setPassword(e.target.value)} placeholder="••••••••"
                className="mt-1.5" autoComplete="current-password" required
              />
            </div>
            {error && (
              <div data-testid="login-error" className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md px-3 py-2">
                {error}
              </div>
            )}
            <Button data-testid="login-submit-button" type="submit" disabled={loading}
              className="w-full bg-[#0F172A] hover:bg-slate-800 h-11">
              {loading ? <><Loader2 className="h-4 w-4 animate-spin mr-2" />{t("loggingIn")}</> : t("login")}
            </Button>
          </form>

          <div className="mt-6 rounded-md bg-slate-50 border border-slate-200 p-4 text-xs text-slate-500">
            <div className="font-medium text-slate-700 mb-1">Demo accounts</div>
            Trainee: <span className="font-mono">TRN1930</span> · Admin: <span className="font-mono">ADM1930</span> · Password: <span className="font-mono">Train@1930</span>
          </div>

          <p className="mt-5 text-sm text-slate-500 text-center">
            New user? <Link to="/register" className="text-blue-600 font-medium hover:underline" data-testid="go-register">Create an account</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
